import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import { useAuth } from "../context/AuthContext";

const CAP_LABELS = {
  ver: "Ver",
  modificar: "Modificar",
  alta: "Alta",
  baja: "Baja",
  alcance_ver: "Alcance al ver",
  alcance_modificar: "Alcance al modificar",
  alcance_alta: "Alcance al crear",
  alcance_baja: "Alcance al eliminar",
};
const CAP_ABBR = { ver: "V", modificar: "M", alta: "A", baja: "B" };
// Mismo orden que el mockup: el alcance de cada acción va justo detrás de su capacidad.
const CAP_INLINE_ORDER = ["ver", "alcance_ver", "modificar", "alcance_modificar", "alta", "alcance_alta", "baja", "alcance_baja"];
const esAlcance = (cap) => cap.startsWith("alcance");

function buscarNodo(nodos, id) {
  for (const n of nodos) {
    if (n.id === id) return n;
    if (n.children) {
      const r = buscarNodo(n.children, id);
      if (r) return r;
    }
  }
  return null;
}

function rutaHastaNodo(nodos, id, acumulado = []) {
  for (const n of nodos) {
    if (n.id === id) return [...acumulado, n];
    if (n.children) {
      const r = rutaHastaNodo(n.children, id, [...acumulado, n]);
      if (r) return r;
    }
  }
  return null;
}

function flattenNodos(nodos, out = []) {
  for (const n of nodos) {
    out.push(n);
    if (n.children?.length) flattenNodos(n.children, out);
  }
  return out;
}

// ── Paleta auxiliar tomada literalmente del mockup (mockups/permisos-roles-mockup.html),
// para que la pantalla real se parezca al máximo. Los tokens que ya existen en el tema
// de la app (bgCard, border, accentBlue...) coinciden casi al pixel con el mockup; el
// resto (ámbar de excepciones, verde/rojo de la tabla de diferencias, tono "elevado"
// de los badges) no tiene equivalente en el tema global y se define aquí, con un
// fallback razonable para el tema claro.
function paletaMockup(isDark) {
  return {
    pillBg: isDark ? "#20252f" : "#eef1f7",
    borderStrong: isDark ? "#4b5568" : "#c8c8be",
    bgElevated: isDark ? "#1f2430" : "#f1f4f9",
    accentSoft: isDark ? "#182236" : "#e8f0f8",
    accent2: isDark ? "#3b82f6" : "#3b6ea5",
    accentDark: isDark ? "#0f1f3d" : "#0f2540",
    textFaint: isDark ? "#6b7684" : "#94a0af",
    amberBorder: "#caa23c",
    amberText: isDark ? "#e0b654" : "#92610a",
    amberBg: isDark ? "#332508" : "#fdf3d9",
    green: "#34d399",
    greenBg: isDark ? "#0f2a20" : "#e8faf1",
    red: "#f87171",
    redBg: isDark ? "#341414" : "#fdeaea",
    infoBg: isDark ? "#13203a" : "#eaf1fb",
    infoBorder: isDark ? "#2c4270" : "#bcd4ee",
    rowSelectedBg: "rgba(37,99,235,0.16)",
    rowSelectedClusterBg: "rgba(37,99,235,0.24)",
  };
}

// ── Pill genérica (selector de modo / rol / usuario) ─────────────────────────
function Pill({ active, onClick, children, mk, c, dashed }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        border: `1px solid ${active ? c.accentBlue : mk.borderStrong}`,
        background: active ? c.accentBlue : mk.pillBg,
        color: active ? "#fff" : c.textPrimary,
        borderRadius: 999, padding: "8px 16px", fontSize: 13, fontWeight: active ? 600 : 500,
        cursor: "pointer", whiteSpace: "nowrap", borderStyle: dashed ? "dashed" : "solid",
      }}
    >
      {children}
    </button>
  );
}

function Dot({ color }) {
  return <span style={{ width: 8, height: 8, borderRadius: "50%", flex: "none", background: color, display: "inline-block" }} />;
}

// ── Badge de capacidad booleana (V/M/A/B), inline en la fila del árbol ───────
function CapBadgeInline({ abbr, active, overridden, primary, onClick, onReset, title, mk, c, labeled }) {
  const size = primary ? 23 : 20;
  const bg = active ? (primary ? c.accentBlue : mk.accent2) : mk.bgElevated;
  // Hojas solo-Ver (botones del head Fríos/Eliminados, etc.): interruptor con texto
  // para que se vea claro Si/No sin depender del badge «V» a la derecha de la columna.
  if (labeled) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
        <button
          type="button"
          onClick={onClick}
          title={title}
          style={{
            boxSizing: "border-box",
            height: 24, borderRadius: 8, padding: "0 10px",
            border: `1px solid ${active ? mk.borderStrong : mk.red}`,
            background: bg,
            color: active ? "#fff" : mk.red,
            fontSize: 11, fontWeight: 700, cursor: "pointer", flex: "none", whiteSpace: "nowrap",
            boxShadow: overridden
              ? `0 0 0 2px ${bg}, 0 0 0 4px ${mk.amberBorder}`
              : (active ? "0 1px 2px rgba(0,0,0,0.35)" : "none"),
          }}
        >
          {active ? "Ver: sí" : "Ver: no"}
        </button>
        {overridden && (
          <button
            type="button"
            onClick={onReset}
            title="Quitar excepción"
            style={{ border: "none", background: "transparent", color: mk.amberText, cursor: "pointer", fontSize: 13, width: 15, height: 15, padding: 0, flex: "none" }}
          >⟲</button>
        )}
      </span>
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <button
        type="button"
        onClick={onClick}
        title={title}
        style={{
          boxSizing: "border-box",
          width: size, height: primary ? 22 : 20, borderRadius: primary ? 7 : 6,
          border: `1px solid ${active ? mk.borderStrong : mk.red}`,
          background: bg,
          color: active ? "#fff" : mk.textFaint,
          fontSize: primary ? 11 : 10, fontWeight: 700, cursor: "pointer", padding: 0, flex: "none",
          // El anillo ámbar de "excepción" se separa del borde con un anillo intermedio
          // del propio color de fondo, para que el borde (rojo si está desactivado) no
          // quede pegado al ámbar y se pueda distinguir bien.
          boxShadow: overridden
            ? `0 0 0 2px ${bg}, 0 0 0 4px ${mk.amberBorder}`
            : (active && primary ? "0 1px 2px rgba(0,0,0,0.35)" : "none"),
        }}
      >
        {abbr}
      </button>
      {overridden && (
        <button
          type="button"
          onClick={onReset}
          title="Quitar excepción"
          style={{ border: "none", background: "transparent", color: mk.amberText, cursor: "pointer", fontSize: 13, width: 15, height: 15, padding: 0, flex: "none" }}
        >⟲</button>
      )}
    </span>
  );
}

// ── Segmentado Propio/Todos compacto, inline en la fila del árbol ────────────
function ScopeSegInline({ value, overridden, onChange, onReset, mk, title }) {
  const opciones = [["propio", "P"], ["todos", "T"]];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <span style={{
        display: "inline-flex", border: `1px solid ${mk.borderStrong}`, borderRadius: 7, overflow: "hidden", flex: "none",
        boxShadow: overridden ? `0 0 0 2px ${mk.amberBorder}` : "none",
      }}>
        {opciones.map(([v, l], i) => (
          <button
            key={v}
            type="button"
            title={`${title}: ${v === "propio" ? "Propio" : "Todos"}`}
            onClick={() => onChange(v)}
            style={{
              width: 17, height: 20, border: "none", padding: 0, cursor: "pointer",
              borderRight: i === 0 ? `1px solid ${mk.borderStrong}` : "none",
              background: value === v ? mk.accent2 : mk.bgElevated,
              color: value === v ? "#fff" : mk.textFaint, fontSize: 9.5, fontWeight: 700,
            }}
          >{l}</button>
        ))}
      </span>
      {overridden && (
        <button
          type="button"
          onClick={onReset}
          title="Quitar excepción"
          style={{ border: "none", background: "transparent", color: mk.amberText, cursor: "pointer", fontSize: 13, width: 15, height: 15, padding: 0, flex: "none" }}
        >⟲</button>
      )}
    </span>
  );
}

function LegendChip({ abbr, label, c }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <b style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", width: 19, height: 18, borderRadius: 5,
        background: c.accentBlue, color: "#fff", fontSize: 10.5, fontWeight: 700,
      }}>{abbr}</b>
      {label}
    </span>
  );
}

function Tag({ ok, children, mk }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, borderRadius: 7, padding: "3px 9px",
      background: ok ? mk.greenBg : mk.redBg, color: ok ? mk.green : mk.red,
    }}>{children}</span>
  );
}

export default function PermisosRoles() {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const mk = useMemo(() => paletaMockup(isDark), [isDark]);
  const { refrescarUsuario } = useAuth();

  const [catalogo, setCatalogo] = useState(null);
  const [modo, setModo] = useState("rol"); // "rol" | "usuario"
  const [rolSel, setRolSel] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioSelId, setUsuarioSelId] = useState(null);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [pendientes, setPendientes] = useState({});
  const [path, setPath] = useState([]);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/permisos/catalogo").then((res) => {
      setCatalogo(res.data);
      if (res.data.roles?.length) setRolSel(res.data.roles[0].id);
      if (res.data.tree?.length) setPath([res.data.tree[0].id]);
    }).catch((e) => {
      setMsg(e.response?.data?.error || "No se pudo cargar el catálogo de permisos");
    });
    api.get("/usuarios").then((res) => {
      const lista = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setUsuarios(lista);
      if (lista.length) setUsuarioSelId(lista[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let activo = true;
    const mensajeErrorCarga = (e) => {
      const status = e.response?.status;
      const apiErr = e.response?.data?.error;
      if (apiErr) return status ? `${apiErr} (${status})` : apiErr;
      if (!e.response) return "Error de red cargando permisos — reintenta en unos segundos";
      return status ? `Error cargando permisos (${status})` : "Error cargando permisos";
    };
    const cargarUna = async () => {
      if (modo === "rol") {
        const res = await api.get(`/permisos/roles/${rolSel}`);
        return res.data;
      }
      const res = await api.get(`/permisos/usuarios/${usuarioSelId}`);
      return res.data;
    };
    const cargar = async () => {
      if (modo === "rol" && !rolSel) return;
      if (modo === "usuario" && !usuarioSelId) return;
      setCargando(true);
      setPendientes({});
      setMsg("");
      try {
        // Un reintento cubre caídas cortas en redeploy de Railway (502/red).
        let data;
        try {
          data = await cargarUna();
        } catch (e1) {
          const reintable = !e1.response || e1.response.status >= 500;
          if (!reintable) throw e1;
          await new Promise((r) => setTimeout(r, 700));
          if (!activo) return;
          data = await cargarUna();
        }
        if (activo) setDatos(data);
      } catch (e) {
        if (activo) setMsg(mensajeErrorCarga(e));
      } finally {
        if (activo) setCargando(false);
      }
    };
    cargar();
    return () => { activo = false; };
  }, [modo, rolSel, usuarioSelId]);

  const nodosFlat = useMemo(() => (catalogo ? flattenNodos(catalogo.tree) : []), [catalogo]);

  const columnas = useMemo(() => {
    if (!catalogo) return [];
    const cols = [{ title: "Secciones del CRM", nodos: catalogo.tree }];
    let nivel = catalogo.tree;
    for (const id of path) {
      const nodo = nivel.find((n) => n.id === id);
      if (!nodo?.children?.length) break;
      cols.push({ title: nodo.label, nodos: nodo.children });
      nivel = nodo.children;
    }
    return cols;
  }, [catalogo, path]);

  const nodoSelId = path[path.length - 1] || null;
  const breadcrumb = useMemo(() => {
    if (!catalogo || !nodoSelId) return [];
    return rutaHastaNodo(catalogo.tree, nodoSelId) || [];
  }, [catalogo, nodoSelId]);

  const rootNode = useMemo(() => {
    if (!catalogo || !path[0]) return null;
    return buscarNodo(catalogo.tree, path[0]);
  }, [catalogo, path]);
  const rootScopeCaps = (rootNode?.caps || []).filter(esAlcance);

  const seleccionarEnColumna = (colIdx, nodeId) => {
    setPath((prev) => [...prev.slice(0, colIdx), nodeId]);
  };

  const overridesSet = useMemo(() => {
    return new Set((datos?.overrides || []).map((o) => `${o.node_id}::${o.cap}`));
  }, [datos]);

  const valorEfectivo = (nodeId, cap) => {
    const key = `${nodeId}::${cap}`;
    if (key in pendientes) {
      const p = pendientes[key];
      if (p !== null) return p;
      // Reset pendiente: en modo usuario cae al valor del rol; en modo rol, al ir a
      // guardar se recalcula el valor por defecto real en el backend.
      if (modo === "usuario") return datos?.efectivoRol?.[nodeId]?.[cap];
      return undefined;
    }
    return datos?.efectivo?.[nodeId]?.[cap];
  };

  const esExcepcionActiva = (nodeId, cap) => {
    const key = `${nodeId}::${cap}`;
    if (key in pendientes) return pendientes[key] !== null;
    return overridesSet.has(key);
  };

  const setPendiente = (nodeId, cap, valor) => {
    setPendientes((prev) => {
      const next = { ...prev, [`${nodeId}::${cap}`]: valor };
      // Activar un hijo (p. ej. cfg_whatsapp) sin el padre (configuracion)
      // dejaba el enlace invisible en el ☰. Si se enciende "ver" en un nodo,
      // se enciende también "ver" de los ancestros que lo tengan apagado.
      if (cap === "ver" && valor === true && catalogo?.tree) {
        const ruta = rutaHastaNodo(catalogo.tree, nodeId) || [];
        for (const ancestro of ruta.slice(0, -1)) {
          if (!(ancestro.caps || []).includes("ver")) continue;
          const keyAnc = `${ancestro.id}::ver`;
          const actual = Object.prototype.hasOwnProperty.call(next, keyAnc)
            ? next[keyAnc]
            : datos?.efectivo?.[ancestro.id]?.ver;
          if (actual === false) next[keyAnc] = true;
        }
      }
      return next;
    });
    setMsg("");
  };

  const quitarExcepcion = (nodeId, cap) => {
    setPendientes((prev) => ({ ...prev, [`${nodeId}::${cap}`]: null }));
    setMsg("");
  };

  const hayPendientes = Object.keys(pendientes).length > 0;

  const guardar = async () => {
    const cambios = Object.entries(pendientes).map(([key, valor]) => {
      const [node_id, cap] = key.split("::");
      return { node_id, cap, valor };
    });
    if (!cambios.length) return;
    setGuardando(true);
    setMsg("");
    try {
      if (modo === "rol") {
        const res = await api.put(`/permisos/roles/${rolSel}`, { cambios });
        setDatos((prev) => ({ ...prev, efectivo: res.data.efectivo, overrides: res.data.overrides }));
      } else {
        const res = await api.put(`/permisos/usuarios/${usuarioSelId}`, { cambios });
        setDatos((prev) => ({ ...prev, efectivo: res.data.efectivo, efectivoRol: res.data.efectivoRol, overrides: res.data.overrides }));
      }
      setPendientes({});
      setMsg("✓ Cambios guardados");
      // Si el rol/usuario editado es el de la sesión actual (p. ej. probando
      // permisos con tu propia cuenta), refresca ya sin esperar al siguiente
      // cambio de pestaña; para otras sesiones abiertas se sincroniza sola.
      refrescarUsuario?.();
    } catch (e) {
      setMsg(e.response?.data?.error || "Error guardando los cambios");
    } finally {
      setGuardando(false);
    }
  };

  const descartar = () => {
    setPendientes({});
    setMsg("");
  };

  const quitarTodasExcepciones = async () => {
    if (modo !== "usuario" || !usuarioSelId) return;
    if (!(await dialogs.confirm("¿Quitar todas las excepciones de este usuario? Volverá a heredar el 100% del rol."))) return;
    setGuardando(true);
    setMsg("");
    try {
      const res = await api.delete(`/permisos/usuarios/${usuarioSelId}`);
      setDatos((prev) => ({ ...prev, efectivo: res.data.efectivo, overrides: [] }));
      setPendientes({});
      setMsg("✓ Excepciones eliminadas");
      refrescarUsuario?.();
    } catch (e) {
      setMsg(e.response?.data?.error || "Error");
    } finally {
      setGuardando(false);
    }
  };

  const usuarioActual = usuarios.find((u) => u.id === usuarioSelId);
  const roleLabelOf = (rolId) => catalogo?.roles?.find((r) => r.id === rolId)?.label || rolId;
  const roleColorOf = (rolId) => catalogo?.roles?.find((r) => r.id === rolId)?.color || mk.textFaint;

  // "Excepciones activas ahora" (guardadas + pendientes sin guardar), para el resumen
  // final y la tabla de diferencias — igual espíritu que el mockup ("resultado final
  // aplicado ahora mismo, combinando rol y excepciones").
  const excepcionesActivas = useMemo(() => {
    if (modo !== "usuario" || !nodosFlat.length) return [];
    const out = [];
    for (const n of nodosFlat) {
      for (const cap of n.caps) {
        if (esExcepcionActiva(n.id, cap)) out.push({ nodeId: n.id, cap });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, nodosFlat, pendientes, overridesSet, datos]);

  const countCap = (cap) => {
    const nodes = nodosFlat.filter((n) => n.caps.includes(cap));
    const total = nodes.length;
    const allowed = nodes.filter((n) => valorEfectivo(n.id, cap) === true).length;
    return { allowed, total };
  };

  const boxStyle = { background: c.bgCard, borderRadius: 16, border: `1px solid ${c.border}` };
  const cardPadStyle = { padding: "20px 22px" };
  const cardTitleStyle = { fontSize: 15, fontWeight: 700, color: c.textPrimary, margin: "0 0 3px", letterSpacing: "-0.01em" };
  const cardDescStyle = { fontSize: 12.5, color: c.textMuted, margin: "0 0 16px", lineHeight: 1.5 };
  const thStyle = { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.03em", color: mk.textFaint, padding: "7px 12px", borderBottom: `1px solid ${c.border}`, fontWeight: 700 };
  const tdStyle = { padding: "9px 12px", borderBottom: `1px solid ${c.border}`, color: c.textPrimary };

  if (!catalogo) {
    return (
      <div style={{ padding: 24, maxWidth: 1680, margin: "0 auto" }}>
        <p style={{ color: c.textMuted }}>{msg || "Cargando catálogo de permisos..."}</p>
      </div>
    );
  }

  const cVer = countCap("ver"), cMod = countCap("modificar"), cAlta = countCap("alta"), cBaja = countCap("baja");

  return (
    <div style={{ padding: 24, maxWidth: 1680, margin: "0 auto" }}>
      {/* Cabecera */}
      <div style={{
        background: `linear-gradient(180deg, ${c.accentBlue} 0%, ${mk.accentDark} 100%)`,
        borderRadius: 16, padding: "22px 26px", marginBottom: 20,
        display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap",
      }}>
        <div>
          <h1 style={{ fontSize: 21, fontWeight: 700, color: "#fff", margin: "0 0 4px", display: "flex", alignItems: "center", letterSpacing: "-0.01em" }}>
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 9,
              background: "rgba(255,255,255,0.14)", fontWeight: 800, fontSize: 14, marginRight: 12, flex: "none",
            }}>VC</span>
            Permisos y roles
          </h1>
          <p style={{ color: "#c3d3ea", fontSize: 13.5, margin: 0, maxWidth: 640, lineHeight: 1.5 }}>
            Configura qué puede ver, editar, crear y eliminar cada rol — y ajusta excepciones puntuales por persona.
          </p>
        </div>
      </div>

      {/* Card: ¿A quién estás configurando? */}
      <div style={{ ...boxStyle, marginBottom: 20 }}>
        <div style={cardPadStyle}>
          <div style={cardTitleStyle}>¿A quién estás configurando?</div>
          <div style={cardDescStyle}>Elige si vas a editar el permiso general de un rol, o una excepción puntual para una persona.</div>

          <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginBottom: 14 }}>
            <Pill active={modo === "rol"} onClick={() => setModo("rol")} mk={mk} c={c}>Por rol</Pill>
            <Pill active={modo === "usuario"} onClick={() => setModo("usuario")} mk={mk} c={c}>Por usuario (excepciones)</Pill>
          </div>

          {modo === "rol" ? (
            <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
              {catalogo.roles.map((r) => (
                <Pill key={r.id} active={rolSel === r.id} onClick={() => setRolSel(r.id)} mk={mk} c={c}>
                  <Dot color={r.color} />{r.label}
                </Pill>
              ))}
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
                {usuarios.map((u) => (
                  <Pill key={u.id} active={usuarioSelId === u.id} onClick={() => setUsuarioSelId(u.id)} mk={mk} c={c}>
                    <Dot color={roleColorOf(u.rol)} />{u.nombre}
                  </Pill>
                ))}
              </div>
              {usuarioActual && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 12, fontSize: 13 }}>
                  <span style={{ fontWeight: 700, color: c.textPrimary }}>{usuarioActual.nombre}</span>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 999,
                    background: mk.pillBg, border: `1px solid ${mk.borderStrong}`, padding: "4px 12px", fontSize: 12, fontWeight: 600, color: c.textMuted,
                  }}>
                    <Dot color={roleColorOf(usuarioActual.rol)} />Rol base: {roleLabelOf(usuarioActual.rol)}
                  </span>
                  <span style={{ color: c.textMuted }}>
                    {(datos?.overrides || []).length === 0
                      ? "Sin excepciones — hereda el rol al 100%"
                      : `${datos.overrides.length} excepción(es) guardada(s) sobre el rol`}
                  </span>
                  {(datos?.overrides || []).length > 0 && (
                    <button
                      onClick={quitarTodasExcepciones}
                      disabled={guardando}
                      style={{
                        border: `1px solid ${mk.borderStrong}`, background: mk.pillBg, color: c.textMuted,
                        borderRadius: 9, padding: "7px 13px", fontSize: 12.5, cursor: "pointer", fontWeight: 600,
                      }}
                    >Quitar todas las excepciones</button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {msg && (
        <div style={{
          padding: "8px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.startsWith("✓") ? mk.greenBg : mk.redBg,
          border: `1px solid ${msg.startsWith("✓") ? mk.green : mk.red}55`,
          color: msg.startsWith("✓") ? mk.green : mk.red,
        }}>
          {msg}
        </div>
      )}

      {/* Card: Árbol de permisos */}
      <div style={{ ...boxStyle, marginBottom: 20 }}>
        <div style={cardPadStyle}>
          <div style={cardTitleStyle}>Árbol de permisos</div>
          <div style={cardDescStyle}>
            Haz clic en un elemento para explorar sus subsecciones — las columnas anteriores se quedan visibles para no perder el contexto.
            Los interruptores V·M·A·B y el alcance P·T se editan directamente en la fila.
          </div>

          {/* Breadcrumb */}
          <div style={{ fontSize: 13, marginBottom: 10, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            {["Secciones del CRM", ...breadcrumb.map((n) => n.label)].map((p, i, arr) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center" }}>
                {i > 0 && <span style={{ margin: "0 7px", color: mk.textFaint }}>›</span>}
                <span style={{ color: i === arr.length - 1 ? c.textPrimary : c.textMuted, fontWeight: i === arr.length - 1 ? 700 : 500 }}>{p}</span>
              </span>
            ))}
          </div>

          {/* Ámbito heredado */}
          {rootScopeCaps.length > 0 && (
            <div style={{
              fontSize: 12, color: c.textMuted, background: mk.bgElevated, border: `1px solid ${c.border}`,
              borderRadius: 9, padding: "9px 13px", marginBottom: 12,
            }}>
              Ámbito heredado de <b style={{ color: c.textPrimary, fontWeight: 700 }}>{rootNode.label}</b> —{" "}
              {rootScopeCaps.map((cap, i) => {
                const label = CAP_LABELS[cap].replace("Alcance al ", "");
                const v = valorEfectivo(rootNode.id, cap);
                const vStr = typeof v === "string" ? v : "todos";
                return (
                  <span key={cap}>
                    {i > 0 && " · "}
                    {label} <span style={{ color: mk.accent2, fontWeight: 700 }}>{vStr === "propio" ? "Propio" : "Todos"}</span>
                  </span>
                );
              })}
              . Se aplica a todas las columnas abiertas de esta rama.
            </div>
          )}

          {/* Leyenda */}
          <div style={{
            fontSize: 12, color: c.textMuted, marginBottom: 16, display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center",
            background: mk.accentSoft, border: `1px solid ${c.border}`, borderRadius: 10, padding: "10px 14px",
          }}>
            <LegendChip abbr="V" label="Ver" c={c} />
            <LegendChip abbr="M" label="Modificar" c={c} />
            <LegendChip abbr="A" label="Alta (crear)" c={c} />
            <LegendChip abbr="B" label="Baja (eliminar)" c={c} />
            <span>P·T junto a V, M o B = alcance de esa acción (Propio / Todos)</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, border: `2px solid ${mk.amberBorder}`, background: "transparent", display: "inline-block" }} />
              Borde ámbar = excepción de usuario
            </span>
          </div>

          {/* Miller columns */}
          {cargando ? (
            <div style={{ padding: 60, textAlign: "center", color: c.textMuted }}>Cargando...</div>
          ) : (
            <div style={{
              display: "flex", gap: 0, flex: "0 1 auto", maxWidth: "100%", overflowX: "auto",
              border: `1px solid ${c.border}`, borderRadius: 14, background: c.bgCard,
            }}>
              {columnas.map((col, colIdx) => (
                <div key={colIdx} style={{
                  flex: "0 0 340px", borderRight: colIdx < columnas.length - 1 ? `1px solid ${c.border}` : "none",
                  display: "flex", flexDirection: "column",
                }}>
                  <div style={{
                    padding: "12px 16px", fontSize: 13, fontWeight: 700, color: c.textPrimary, background: mk.accentSoft,
                    borderBottom: `1px solid ${c.border}`, position: "sticky", top: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {col.title}
                  </div>
                  <div style={{ flex: 1 }}>
                    {col.nodos.map((n) => {
                      const activo = path[colIdx] === n.id;
                      const hasChildren = !!n.children?.length;
                      const restringido = Array.isArray(n.restrictedTo);
                      const capsNodo = Array.isArray(n.caps) ? n.caps : [];
                      const soloVer = !hasChildren && capsNodo.length === 1 && capsNodo[0] === "ver";
                      return (
                        <div key={n.id} style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                          padding: "9px 12px 9px 10px", borderBottom: `1px solid ${c.border}`,
                          borderLeft: `3px solid ${activo ? mk.accent2 : "transparent"}`,
                          background: activo ? mk.rowSelectedBg : "transparent",
                        }}>
                          <div
                            onClick={() => seleccionarEnColumna(colIdx, n.id)}
                            style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0, cursor: "pointer" }}
                            title={n.label}
                          >
                            <span style={{ width: 14, flex: "none", color: mk.textFaint, fontSize: 14, textAlign: "center" }}>
                              {hasChildren ? "›" : ""}
                            </span>
                            <span style={{
                              fontSize: 13, color: activo ? c.textPrimary : c.textSecondary, fontWeight: activo ? 700 : 500,
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            }}>
                              {n.label}{restringido ? " 🔒" : ""}
                            </span>
                          </div>
                          <div style={{
                            display: "flex", alignItems: "center", gap: 4, flex: "none",
                            background: activo ? mk.rowSelectedClusterBg : mk.pillBg, borderRadius: 8, padding: "3px 5px",
                          }}>
                            {CAP_INLINE_ORDER.filter((cap) => capsNodo.includes(cap)).map((cap) =>
                              esAlcance(cap) ? (
                                <ScopeSegInline
                                  key={cap}
                                  title={CAP_LABELS[cap]}
                                  value={typeof valorEfectivo(n.id, cap) === "string" ? valorEfectivo(n.id, cap) : "todos"}
                                  overridden={esExcepcionActiva(n.id, cap)}
                                  onChange={(v) => setPendiente(n.id, cap, v)}
                                  onReset={() => quitarExcepcion(n.id, cap)}
                                  mk={mk}
                                />
                              ) : (
                                <CapBadgeInline
                                  key={cap}
                                  abbr={CAP_ABBR[cap]}
                                  primary={cap === "ver"}
                                  labeled={soloVer && cap === "ver"}
                                  active={valorEfectivo(n.id, cap) === true}
                                  overridden={esExcepcionActiva(n.id, cap)}
                                  onClick={() => setPendiente(n.id, cap, !(valorEfectivo(n.id, cap) === true))}
                                  onReset={() => quitarExcepcion(n.id, cap)}
                                  title={CAP_LABELS[cap]}
                                  mk={mk}
                                  c={c}
                                />
                              )
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Card: Resumen de permisos efectivos */}
      <div style={{ ...boxStyle, marginBottom: 20 }}>
        <div style={cardPadStyle}>
          <div style={cardTitleStyle}>Resumen de permisos efectivos</div>
          <div style={cardDescStyle}>
            Resultado final aplicado a <b style={{ color: c.textPrimary }}>{modo === "rol" ? roleLabelOf(rolSel) : (usuarioActual?.nombre || "")}</b> ahora mismo, combinando rol y excepciones.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14, padding: "4px 0 6px" }}>
            <div style={{ background: mk.bgElevated, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontSize: 21, fontWeight: 700, color: c.textPrimary }}>{cVer.allowed}/{cVer.total}</div>
              <div style={{ fontSize: 11.5, color: c.textMuted, marginTop: 3 }}>Elementos visibles (Ver)</div>
            </div>
            <div style={{ background: mk.bgElevated, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontSize: 21, fontWeight: 700, color: c.textPrimary }}>{cMod.allowed}/{cMod.total}</div>
              <div style={{ fontSize: 11.5, color: c.textMuted, marginTop: 3 }}>Editables (Modificar)</div>
            </div>
            <div style={{ background: mk.bgElevated, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontSize: 21, fontWeight: 700, color: c.textPrimary }}>{cAlta.allowed}/{cAlta.total}</div>
              <div style={{ fontSize: 11.5, color: c.textMuted, marginTop: 3 }}>Altas permitidas</div>
            </div>
            <div style={{ background: mk.bgElevated, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontSize: 21, fontWeight: 700, color: cBaja.allowed > 0 ? mk.amberText : c.textPrimary }}>{cBaja.allowed}/{cBaja.total}</div>
              <div style={{ fontSize: 11.5, color: c.textMuted, marginTop: 3 }}>Bajas permitidas</div>
            </div>
            {modo === "usuario" && (
              <div style={{ background: mk.bgElevated, borderRadius: 12, padding: "12px 14px" }}>
                <div style={{ fontSize: 21, fontWeight: 700, color: excepcionesActivas.length > 0 ? mk.amberText : c.textPrimary }}>{excepcionesActivas.length}</div>
                <div style={{ fontSize: 11.5, color: c.textMuted, marginTop: 3 }}>Excepciones activas</div>
              </div>
            )}
          </div>

          {modo === "usuario" && (
            excepcionesActivas.length > 0 ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, margin: "10px 0 6px" }}>
                  Diferencias respecto al rol {roleLabelOf(usuarioActual?.rol)}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Elemento · capacidad</th>
                      <th style={thStyle}>Valor del rol</th>
                      <th style={thStyle}>Valor del usuario (prevalece)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excepcionesActivas.map(({ nodeId, cap }) => {
                      const node = nodosFlat.find((n) => n.id === nodeId);
                      const roleValue = datos?.efectivoRol?.[nodeId]?.[cap];
                      const userValue = valorEfectivo(nodeId, cap);
                      const isScope = esAlcance(cap);
                      const fmt = (v) => (isScope ? (v === "propio" ? "Propio" : "Todos") : (v ? "Permitido" : "No permitido"));
                      const borderColor = !isScope && userValue && !roleValue ? mk.green : (!isScope && !userValue && roleValue ? mk.amberBorder : "transparent");
                      return (
                        <tr key={`${nodeId}|${cap}`}>
                          <td style={{ ...tdStyle, borderLeft: `3px solid ${borderColor}` }}>{node?.label || nodeId} · {CAP_LABELS[cap]}</td>
                          <td style={tdStyle}><Tag ok={isScope || !!roleValue} mk={mk}>{fmt(roleValue)}</Tag></td>
                          <td style={tdStyle}><Tag ok={isScope || !!userValue} mk={mk}>{fmt(userValue)}</Tag></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : (
              <div style={{
                borderRadius: 12, padding: "13px 16px", fontSize: 13, lineHeight: 1.55, marginTop: 12,
                border: `1px solid ${c.border}`, background: mk.bgElevated, color: c.textMuted,
              }}>
                <b style={{ display: "block", marginBottom: 3, fontWeight: 700, color: c.textPrimary }}>Sin excepciones</b>
                {usuarioActual?.nombre} hereda el 100% de los permisos de su rol {roleLabelOf(usuarioActual?.rol)}. Cualquier control que cambies en el árbol, en modo "Por usuario", crea una excepción que prevalece sobre el rol solo para {usuarioActual?.nombre}.
              </div>
            )
          )}
        </div>
      </div>

      {/* Barra de acciones */}
      <div style={{
        position: "sticky", bottom: 16, display: "flex", gap: 12, alignItems: "center",
        padding: "12px 18px", borderRadius: 12, background: c.bgCard, border: `1px solid ${c.border}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
      }}>
        <span style={{ fontSize: 13, color: c.textMuted }}>
          {hayPendientes ? `${Object.keys(pendientes).length} cambio(s) sin guardar` : "Sin cambios pendientes"}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
          <button
            onClick={descartar}
            disabled={!hayPendientes || guardando}
            style={{
              padding: "8px 18px", borderRadius: 8, border: `1px solid ${c.border}`, background: "transparent",
              color: c.textSecondary, fontWeight: 600, fontSize: 13, cursor: hayPendientes ? "pointer" : "default",
              opacity: hayPendientes ? 1 : 0.5,
            }}
          >Descartar</button>
          <button
            onClick={guardar}
            disabled={!hayPendientes || guardando}
            style={{
              padding: "8px 20px", borderRadius: 8, border: "none", background: "#16a34a", color: "#fff",
              fontWeight: 700, fontSize: 13, cursor: hayPendientes ? "pointer" : "default",
              opacity: hayPendientes ? 1 : 0.5,
            }}
          >{guardando ? "Guardando..." : "Guardar cambios"}</button>
        </div>
      </div>
    </div>
  );
}
